/**
 * Criteria multi-select — a grouped dropdown menu.
 *
 * Replaces a horizontally-scrolling chip strip. With 13 criteria a strip hides
 * most of them off-screen and was the last thing on the page that scrolled
 * sideways.
 *
 * The pattern is the standard faceted-filter menu (GitHub labels, Linear
 * filters, Notion properties): a trigger summarising the selection, opening a
 * grouped checkbox list. Multi-select stays open between choices, because
 * picking several is the normal case — closing after each would force the user
 * to reopen it repeatedly.
 */

import { useEffect, useRef } from 'react';

export interface CriteriaOption {
  id: string;
  label: string;
  formula: string;
  needsNutrition: boolean;
  group: 'value' | 'density' | 'amount';
}

interface Props {
  options: CriteriaOption[];
  selected: string[];
  onToggle: (id: string) => void;
  /** Which selected criterion leads the display. Null = the first selected. */
  lead?: string | null;
  onLeadChange?: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Products currently rankable, shown so thin criteria are obvious. */
  eligible?: number | null;
}

const GROUP_LABEL: Record<CriteriaOption['group'], string> = {
  value: 'Value for money',
  density: 'Per 100 kcal',
  amount: 'Amount per 100 g / ml',
};

const GROUP_ORDER: Array<CriteriaOption['group']> = ['value', 'density', 'amount'];

export function CriteriaPicker({
  options,
  selected,
  onToggle,
  open,
  onOpenChange,
  eligible = null,  lead = null,
  onLeadChange,
}: Props): React.JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    const onPointer = (e: PointerEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, onOpenChange]);

  const chosen = options.filter((o) => selected.includes(o.id));
  const summary =
    chosen.length === 0
      ? 'Choose criteria'
      : chosen.length === 1
        ? chosen[0]!.label
        : `${chosen.length} criteria`;

  return (
    <div className="crit" ref={wrapRef}>
      <button
        type="button"
        className="crit-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => onOpenChange(!open)}
      >
        <span className="crit-icon" aria-hidden="true">
          ⚑
        </span>
        <span className="crit-label">{summary}</span>
        {chosen.length > 1 && <span className="crit-badge">{chosen.length}</span>}
        <span className="crit-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="crit-menu" role="listbox" aria-multiselectable="true" aria-label="Ranking criteria">
          <div className="crit-menu-head">
            <span>Rank by</span>
            {chosen.length > 1 && (
              <span className="crit-hint">Combined across {chosen.length}</span>
            )}
          </div>

          <div className="crit-list">
            {GROUP_ORDER.map((group) => {
              const inGroup = options.filter((o) => o.group === group);
              if (inGroup.length === 0) return null;
              return (
                <div className="crit-group" key={group}>
                  <div className="crit-group-label">{GROUP_LABEL[group]}</div>
                  {inGroup.map((o) => {
                    const on = selected.includes(o.id);
                    const isLead = on && (lead === o.id || (lead === null && selected[0] === o.id));
                    return (
                      <div className="crit-row" key={o.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        className={`crit-opt${on ? ' is-on' : ''}`}
                        onClick={() => onToggle(o.id)}
                        title={o.formula}
                      >
                        <span className={`crit-box${on ? ' is-on' : ''}`} aria-hidden="true">
                          {on ? '✓' : ''}
                        </span>
                        <span className="crit-opt-text">
                          <span className="crit-opt-name">{o.label}</span>
                          <span className="crit-opt-formula">{o.formula}</span>
                        </span>
                        {/* Nutrition coverage is thin, so say so before it is picked. */}
                        {o.needsNutrition && <span className="crit-warn" title="Limited nutrition data">◔</span>}
                      </button>
                      {/*
                        Which criterion leads. Shown only when several are
                        selected, because with one there is nothing to lead.
                        It decides the number each row displays and breaks ties
                        in the combined score.
                      */}
                      {on && selected.length > 1 && onLeadChange !== undefined && (
                        <button
                          type="button"
                          className={`crit-lead${isLead ? ' is-lead' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onLeadChange(o.id);
                          }}
                          aria-pressed={isLead}
                          aria-label={`Show ${o.label} first`}
                          title={isLead ? 'Leading — shown on every row' : 'Make this the number shown'}
                        >
                          {isLead ? '★' : '☆'}
                        </button>
                      )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="crit-menu-foot">
            {eligible !== null && <span>{eligible.toLocaleString()} products ranked</span>}
            <button type="button" onClick={() => onOpenChange(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
